'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Menu, Search, Bell, ChevronDown, LogOut, User, Settings } from 'lucide-react';
import AppLogo from './ui/AppLogo';

interface TopbarProps {
  onMobileMenuOpen: () => void;
  sidebarCollapsed?: boolean;
}

export default function Topbar({ onMobileMenuOpen }: TopbarProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const notifications = [
    { id: 'notif-1', text: 'New lesson available in Python for Data Science', time: '2m ago', unread: true },
    { id: 'notif-2', text: 'Priya Sharma replied to your thread', time: '14m ago', unread: true },
    { id: 'notif-3', text: 'You earned the "7-Day Streak" badge!', time: '1h ago', unread: true },
    { id: 'notif-4', text: 'Course "Intro to Health Informatics" updated', time: '3h ago', unread: false },
    { id: 'notif-5', text: 'New resource shared in AI community', time: '5h ago', unread: false },
  ];

  return (
    <header className="h-16 bg-card border-b border-border flex items-center px-4 lg:px-6 gap-4 sticky top-0 z-20">
      {/* Mobile menu button */}
      <button
        onClick={onMobileMenuOpen}
        className="lg:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      {/* Mobile logo */}
      <Link href="/" className="flex items-center gap-2 lg:hidden">
        <AppLogo size={28} />
        <span className="font-bold text-base text-foreground">EugeneTechRN</span>
      </Link>

      {/* Search */}
      <div className="flex-1 max-w-md hidden md:block">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search courses, lessons, community..."
            className="input-field pl-9 py-2 text-sm h-9"
            onFocus={() => setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
          />
          {searchOpen && (
            <div className="absolute top-full mt-2 left-0 right-0 card shadow-modal p-2 z-50 fade-in">
              <p className="section-label px-2 mb-2">Quick access</p>
              {[
                { label: 'Machine Learning Fundamentals', type: 'Course' },
                { label: 'Python for Data Science', type: 'Course' },
                { label: 'Intro to Health Informatics', type: 'Course' },
              ].map((item) => (
                <Link
                  key={`search-${item.label}`}
                  href="/course-catalogue"
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted text-sm transition-colors"
                >
                  <Search size={14} className="text-muted-foreground" />
                  <span className="flex-1 text-foreground">{item.label}</span>
                  <span className="text-xs text-muted-foreground">{item.type}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1" />

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={() => { setNotifOpen(!notifOpen); setUserMenuOpen(false); }}
          className="relative p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
          aria-label="Notifications"
        >
          <Bell size={20} />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-danger rounded-full" />
        </button>
        {notifOpen && (
          <div className="absolute right-0 top-full mt-2 w-80 card shadow-modal z-50 fade-in overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <p className="font-semibold text-sm text-foreground">Notifications</p>
              <span className="text-xs text-primary font-medium cursor-pointer hover:underline">Mark all read</span>
            </div>
            <div className="max-h-72 overflow-y-auto scrollbar-thin">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  className={`flex gap-3 px-4 py-3 hover:bg-muted cursor-pointer transition-colors border-b border-border last:border-0 ${n.unread ? 'bg-secondary/30' : ''}`}
                >
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${n.unread ? 'bg-primary' : 'bg-transparent'}`} />
                  <div>
                    <p className="text-sm text-foreground leading-snug">{n.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">{n.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* User menu */}
      <div className="relative">
        <button
          onClick={() => { setUserMenuOpen(!userMenuOpen); setNotifOpen(false); }}
          className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-muted transition-colors"
        >
          <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold">
            SA
          </div>
          <span className="hidden md:block text-sm font-medium text-foreground">Sophia</span>
          <ChevronDown size={14} className="hidden md:block text-muted-foreground" />
        </button>
        {userMenuOpen && (
          <div className="absolute right-0 top-full mt-2 w-52 card shadow-modal z-50 fade-in overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="text-sm font-semibold text-foreground">Sophia Anderson</p>
              <p className="text-xs text-muted-foreground">sophia@eugenetechrn.io</p>
            </div>
            {[
              { icon: <User size={14} />, label: 'My Profile', href: '/learner-dashboard' },
              { icon: <Settings size={14} />, label: 'Settings', href: '/learner-dashboard' },
            ].map((item) => (
              <Link
                key={`usermenu-${item.label}`}
                href={item.href}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                onClick={() => setUserMenuOpen(false)}
              >
                {item.icon}
                {item.label}
              </Link>
            ))}
            <div className="border-t border-border">
              <Link
                href="/sign-up-login-screen"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-danger hover:bg-danger/5 transition-colors"
                onClick={() => setUserMenuOpen(false)}
              >
                <LogOut size={14} />
                Sign out
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}